import { useEffect, useState } from 'react';

import { InvoiceStatusEnum, InvoiceTypeEnum, InvoiceViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { Box, Container, Flex, Headings, Link, Spacer, Text } from '@foradevelopment/fora-ui';
import { dateStringFormatter } from '@fora-monorepo/date-utils';

import { getInvoiceEvents } from '../../../redux/invoice/invoiceThunks';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getStatusText } from '../../utils';
import { getStatusExplanationText } from '../../utils/getStatusExplanationText';
import { InvoiceEventLog } from '../InvoiceEventLog';
import { InvoiceInfoBox } from '../InvoiceInfoBox';
import { useTypedTranslation } from '../../../i18n/i18n';

export const InvoicesDetail = ({
  invoiceNumber,
  invoiceDate,
  invoiceDueDate,
  amountLeftToPay,
  ocr,
  status,
  events,
  invoiceType,
  showCsvDownloadButton,
}: InvoiceViewModel) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const bgNumber = useAppSelector(state => state.invoice.bgNumber);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!events) {
      setLoadingHistory(true);
      dispatch(getInvoiceEvents({ invoiceNumber })).then(data => {
        if (getInvoiceEvents.rejected.match(data)) {
          setError(true);
        }
        setLoadingHistory(false);
      });
    }
  }, [events, dispatch, invoiceNumber]);

  return (
    <>
      {status !== InvoiceStatusEnum.Paid && (
        <Container marginBottom={40}>
          <Headings.H4 marginBottom={6}>{getStatusText(status, t)}</Headings.H4>
          <Text max-width={398}>{getStatusExplanationText(status, t)}</Text>
          {(status === InvoiceStatusEnum.InstallmentPlan || status === InvoiceStatusEnum.DebtCollection) && (
            <Spacer marginTop={6}>
              <Link to="https://www.svea.com/sv-se">{t('section.invoiceDetails.readMoreLink')}</Link>
            </Spacer>
          )}
        </Container>
      )}
      <Flex columnGap={50} rowGap={20} marginBottom={40} flexDirection="row" width="100%" flexWrap="wrap">
        <Box minWidth="3.75rem">
          <Text>{t('list.invoiceEvents.invoiceDate')}</Text>
          <Text>{dateStringFormatter(invoiceDate)}</Text>
        </Box>
        <Box minWidth="3.75rem">
          <Text>{t('table.invoice.columnInvoiceDueDate')}</Text>
          <Text>{invoiceDueDate ? dateStringFormatter(invoiceDueDate) : '-'}</Text>
        </Box>
        {bgNumber && invoiceType !== InvoiceTypeEnum.Credit && (
          <Box minWidth="3.75rem">
            <Text>{t('list.invoiceEvents.bankAccountNumber')}</Text>
            <Text>{bgNumber}</Text>
          </Box>
        )}
        {ocr && invoiceType !== InvoiceTypeEnum.Credit && (
          <Box minWidth="3.75rem">
            <Text>{t('table.invoice.columnInvoiceNumber')}</Text>
            <Text>{ocr}</Text>
          </Box>
        )}
      </Flex>
      <InvoiceInfoBox
        invoiceNumber={invoiceNumber}
        ocrNumber={ocr}
        amountLeftToPay={amountLeftToPay}
        showCsvDownloadButton={showCsvDownloadButton}
      />
      <InvoiceEventLog
        title={t('section.invoiceOverview.title')}
        list={events?.length ? events : []}
        loading={loadingHistory}
        error={error}
      />
    </>
  );
};
